import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import authService from '../services/authService';
import conversationService, { 
  Conversation, 
  DialogueItem,
  DialogueMessage as DialogueMessageType,
  ConversationQuestion as DialogueQuestionType
} from '../services/conversationService';

// Component imports
import LoadingSpinner from '../components/LoadingSpinner';
import DialogueMessage from '../components/walkthestory/DialogueMessage';
import DialogueQuestion from '../components/walkthestory/DialogueQuestion';
import CharacterPortrait from '../components/walkthestory/CharacterPortrait';
import ConversationHeader from '../components/walkthestory/ConversationHeader';

interface LocationState {
  conversationId?: string;
  character?: string;
  category?: string;
}

// Add type for tracking info
interface DialogueItemWithTracking extends DialogueItem {
  uniqueKey?: string;
  sequenceIndex?: number;
  answered?: boolean;
}

const WalkTheStoryPage: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0);
  const [visibleDialogues, setVisibleDialogues] = useState<DialogueItemWithTracking[]>([]);
  const [waitingForAnswer, setWaitingForAnswer] = useState(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const [score, setScore] = useState(0);
  const [conversationSessionId, setConversationSessionId] = useState<string | null>(null);
  const [skipTyping, setSkipTyping] = useState(false);

  // Ensure backend login
  useEffect(() => {
    if (isAuthenticated && user) {
      const localUser = authService.getCurrentUser();
      if (!localUser) {
        const syncUser = async () => {
          try {
            const accessToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
              }
            });
            await authService.loginWithAuth0(user, accessToken);
          } catch (err) {
            console.error('Failed to sync user with backend:', err);
          }
        };
        syncUser();
      }
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);

  // Load conversation data
  useEffect(() => {
    const fetchConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let conversationData;
        
        // If a specific conversation ID was provided
        if (state?.conversationId) {
          const response = await conversationService.getConversation(state.conversationId);
          conversationData = response.data.data.conversation;
        } 
        // If a character was specified
        else if (state?.character) {
          const response = await conversationService.getConversationsByCharacter(state.character);
          const conversations = response.data.data.conversations;
          if (conversations.length > 0) {
            conversationData = conversations[0];
          } else {
            setError("No conversations found for this character.");
          }
        } 
        // Default to random conversation with optional category filter
        else {
          const filters = state?.category ? { category: state.category } : {};
          const response = await conversationService.getRandomConversations({ 
            count: 1,
            ...filters
          });
          
          const conversations = response.data.data.conversations;
          if (conversations.length > 0) {
            conversationData = conversations[0];
          } else {
            setError("No conversations available.");
          }
        }
        
        if (conversationData) {
          setConversation(conversationData);
          // Start with just the first dialogue item visible
          setVisibleDialogues([conversationData.dialogue[0]]);
          
          // Check for existing progress if authenticated
          if (isAuthenticated && user) {
            try {
              const progressResponse = await conversationService.getUserProgress(conversationData._id);
              if (progressResponse.data.data.progress) {
                const progress = progressResponse.data.data.progress;
                // Resume from last position if not completed
                if (!progress.completed && progress.lastCompletedIndex > 0) {
                  const resumeIndex = Math.min(progress.lastCompletedIndex + 1, conversationData.dialogue.length - 1);
                  setCurrentDialogueIndex(resumeIndex);
                  // Show all dialogues up to resume point
                  setVisibleDialogues(conversationData.dialogue.slice(0, resumeIndex + 1));
                }
              }
            } catch (err) {
              // No existing progress, start from beginning
              console.log('Starting new conversation');
            }
          }
        }
      } catch (err) {
        console.error("Error loading conversation:", err);
        setError("Failed to load the conversation. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchConversation();
  }, [state, isAuthenticated, user]);

  // Handle progression through dialogue
  const advanceDialogue = async () => {
    if (!conversation || waitingForAnswer) return;
    
    // If we're at the end of the conversation
    if (currentDialogueIndex >= conversation.dialogue.length - 1) {
      // Save completion for authenticated users
      if (isAuthenticated && user) {
        try {
          await conversationService.updateProgress(conversation._id, {
            lastCompletedIndex: currentDialogueIndex,
            completed: true
          });
        } catch (err) {
          console.error('Failed to save progress:', err);
        }
      }
      
      // Handle completion - navigate to results or summary
      navigate('/results', { 
        state: { 
          source: 'walkthestory',
          conversationId: conversation._id,
          score,
          characterName: conversation.mainCharacter
        } 
      });
      return;
    }
    
    // Move to the next dialogue item
    const nextIndex = currentDialogueIndex + 1;
    setCurrentDialogueIndex(nextIndex);
    
    // Add only the new dialogue item
    setVisibleDialogues(prev => {
      // Check if this dialogue is already in the list
      const exists = prev.some(d => d.sequenceIndex === nextIndex);
      if (exists) return prev;

      const nextDialogueItem = conversation.dialogue[nextIndex];
      return [...prev, {
        ...nextDialogueItem,
        uniqueKey: `${nextIndex}-${Date.now()}`,
        sequenceIndex: nextIndex,
        answered: false
      }];
    });
    
    // If it's a question, pause for user input
    if (conversation.dialogue[nextIndex].type === 'question') {
      setWaitingForAnswer(true);
      setAutoplayPaused(true);
    }
    
    // Save progress for authenticated users
    if (isAuthenticated && user && nextIndex % 3 === 0) { // Save every 3 dialogues
      try {
        await conversationService.updateProgress(conversation._id, {
          lastCompletedIndex: nextIndex
        });
      } catch (err) {
        console.error('Failed to save progress:', err);
      }
    }
  };

  // Handle question answers
  const handleAnswerSelected = async (questionIndex: number, optionIndex: number, isCorrect: boolean, points: number, nextDialogueIndex?: number) => {
    setWaitingForAnswer(false);
    
    // Update score
    if (isCorrect) {
      setScore(prev => prev + points);
    }

    // Mark the question as answered
    setVisibleDialogues(prev => 
      prev.map(item => 
        item.sequenceIndex === questionIndex 
          ? { ...item, answered: true }
          : item
      )
    );
    
    // Submit answer for authenticated users
    if (isAuthenticated && user && conversation) {
      try {
        await conversationService.submitAnswer({
          conversationId: conversation._id,
          questionIndex: currentDialogueIndex,
          selectedOptionIndex: optionIndex
        });
      } catch (err) {
        console.error('Failed to submit answer:', err);
      }
    }
    
    // If there's a specific next dialogue index
    if (nextDialogueIndex !== undefined && conversation) {
      // Jump to specific dialogue based on answer
      setCurrentDialogueIndex(nextDialogueIndex - 1); // -1 because we'll advance after this
    }
    
    // Continue dialogue flow after a short delay
    setTimeout(() => {
      setAutoplayPaused(false);
      advanceDialogue();
    }, 1500);
  };

  // Handle button click to continue dialogue
  const handleContinue = () => {
    if (!waitingForAnswer) {
      // If text is still typing, skip it
      setSkipTyping(true);
      advanceDialogue();
    }
  };

  // Return to home
  const handleExit = () => {
    navigate('/');
  };

  // Toggle autoplay
  const toggleAutoplay = () => {
    setAutoplayPaused(prev => !prev);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <LoadingSpinner />
        <p className="ml-3 text-lg text-gray-600">Loading conversation...</p>
      </div>
    );
  }

  // Render error state
  if (error || !conversation) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-100 p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-6">{error || "Failed to load conversation data."}</p>
          <button 
            onClick={handleExit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="walk-story-container min-h-screen flex flex-col">
      {/* Background matching the setting */}
      <div 
        className="walk-story-background absolute inset-0 z-0 bg-cover bg-center" 
        style={{ 
          backgroundImage: conversation.setting.backgroundImageUrl 
            ? `url(${conversation.setting.backgroundImageUrl})` 
            : 'url(/images/backgrounds/default-biblical.jpg)' 
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      </div>
      
      {/* Main content area */}
      <div className="flex-grow z-10 overflow-y-auto py-4 px-2 md:px-4 pt-6">
        <div className="max-w-4xl mx-auto">
          {/* Render visible dialogue items */}
          {visibleDialogues.map((dialogueItem) => (
            <div 
              key={dialogueItem.uniqueKey || `${dialogueItem.sequenceIndex}-fallback`} 
              className="mb-4 dialogue-item"
            >
              {dialogueItem.type === 'message' && dialogueItem.message && (
                <DialogueMessage 
                  message={dialogueItem.message}
                  isLatest={dialogueItem.sequenceIndex === currentDialogueIndex}
                  skipTyping={skipTyping}
                />
              )}
              
              {dialogueItem.type === 'question' && dialogueItem.question && (
                <DialogueQuestion 
                  question={dialogueItem.question}
                  onAnswerSelected={(optionIndex: number, isCorrect: boolean, points: number, nextIndex?: number) => 
                    handleAnswerSelected(dialogueItem.sequenceIndex || 0, optionIndex, isCorrect, points, nextIndex)
                  }
                  isLatest={dialogueItem.sequenceIndex === currentDialogueIndex}
                  disabled={!waitingForAnswer || dialogueItem.answered === true}
                  showCorrectAnswer={dialogueItem.answered === true}
                />
              )}
            </div>
          ))}
          
          {/* Spacer at the bottom to ensure content is visible above controls */}
          <div className="h-24"></div>
        </div>
      </div>
      
      {/* Controls fixed at the bottom */}
      <div className="walk-story-controls fixed bottom-0 left-0 right-0 bg-gray-800 bg-opacity-80 backdrop-blur-sm z-20 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex-1">
            <span className="text-white font-semibold">
              {currentDialogueIndex + 1}/{conversation.dialogue.length}
            </span>
          </div>
          
          <button 
            onClick={handleContinue}
            disabled={waitingForAnswer}
            className={`continue-button ${
              waitingForAnswer ? 'disabled' : ''
            }`}
          >
            {currentDialogueIndex >= conversation.dialogue.length - 1 ? 'Finish' : 'Continue'}
          </button>
          
          <div className="flex-1 flex justify-end">
            <span className="text-white font-semibold">
              Score: {score}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalkTheStoryPage; 